import React, { useEffect, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import { HelpfulQuestion } from '@buoy-components/user-feedback';
import styled from 'styled-components';
import { IPeekDifferential } from '@buoyhealth/common.buoy-types';
import { Link } from '@buoyhealth/common.buoy-button';
import { trackStructuredEvent } from 'src/analytics';
import useInterview from '../../hooks/useInterview';
import { trackPeekDifferentialSeen } from '../../analytics';
import useInterviewType from '../../hooks/useInterviewType';
import { withTheme } from '@buoyhealth/common.buoy-theme';
import useIsPolaris from '../../hooks/useIsPolaris';

interface IPeekDifferentialProps {
  item: IPeekDifferential;
}

const Container = styled.div`
  ${({ theme }) => theme.typography.variants.primary.small};
  font-size: 16px;

  p {
    margin: 16px 0;

    &:first-of-type {
      margin-top: 0;
    }

    &:last-of-type {
      margin-bottom: 5px;
    }
  }

  & a {
    font-size: 16px;
  }

  ol {
    padding-inline-start: 40px;
    margin: 16px 0;

    li {
      list-style-type: decimal;
      margin: 5px 0;
    }
  }
`;

const PolarisContainer = styled.div`
  background-color: ${({ theme }) => theme.palette.blue[0]};
  padding: 24px 12px 12px;
  margin-bottom: 24px;
  border-radius: 8px;
`;

export const StyledDivider = styled(Divider)`
  border-color: ${({ theme }) => theme.palette.gray[40]};
  padding-top: 10px;
`;

export const HelpfulContainer = styled.div`
  .MuiIconButton-root {
    svg {
      height: 30px;
      width: 30px;
    }
  }

  .BuoyUserFeedback__HelpfulQuestion__Container > div {
    justify-content: flex-start;

    .MuiIconButton-root:first-of-type {
      margin-left: 12px;
    }
  }
`;

const getLikelyAndOthersText = (likely?: number, others?: number) => {
  if (likely) {
    return `There ${
      likely === 1 ? 'is' : 'are'
    } ${likely} other likely condition${likely === 1 ? '' : 's'}${
      others
        ? `, and ${others} other possible, but unlikely, condition${others === 1 ? '' : 's'}`
        : ''
    } being considered.`;
  }
  if (others) {
    return `There ${others === 1 ? 'is' : 'are'} ${others} other possible, but unlikely, condition${
      others === 1 ? '' : 's'
    } being considered.`;
  }
  return '';
};

const PeekDifferential = (props: IPeekDifferentialProps) => {
  const { differential, belief, likely, others, links } = props.item;
  const interview = useInterview();
  const interviewType = useInterviewType();
  const isPolaris = useIsPolaris();

  const shouldRenderNull = useMemo(
    () => !differential?.length || !belief || !links?.length,
    [belief, differential?.length, links?.length],
  );

  useEffect(() => {
    if (!shouldRenderNull && interview.token) {
      trackPeekDifferentialSeen(interview.token, differential.join(', '), interviewType);
    }
  }, [differential, interview.token, interviewType, shouldRenderNull]);

  if (shouldRenderNull) return null;

  const Inner = isPolaris ? PolarisContainer : React.Fragment;

  return (
    <Grid item>
      <Container className="BuoyInterviews__PeekDifferential">
        <Inner>
          <p>Based on what you’ve said so far, the most likely conditions right now are:</p>
          <ol>
            {differential.map((item, index) => {
              const formattedCondition = `${item.charAt(0).toUpperCase() + item.slice(1)}`;
              return (
                <li key={item}>
                  {links[index] ? (
                    <Link href={`/learn${links[index]}`} rel="noreferrer" target="_blank">
                      {formattedCondition}
                    </Link>
                  ) : (
                    formattedCondition
                  )}
                </li>
              );
            })}
          </ol>
          <p>
            We have a <b>{Math.round(belief * 100)}% belief</b> in these top conditions so far.{' '}
            {getLikelyAndOthersText(likely, others)}
          </p>
          <HelpfulContainer>
            <HelpfulQuestion
              snowplowProps={{ action: 'peek-differential' }}
              trackingCallback={trackStructuredEvent}
              withBuoyTheme
            />
          </HelpfulContainer>
        </Inner>
        <p>Continue answering a few more questions to get your final results.</p>
        <StyledDivider />
      </Container>
    </Grid>
  );
};

export default withTheme(PeekDifferential);
